import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  buildCareTimeline, buildCareTimelineExport, type CareTimelineCadence, type CareTimelineFact,
  type GaitLog, type Meal, type PetEvent, type Treatment, type TreatmentLog, type Visit,
} from '@petrhythm/core';
import { getAttachment, repository } from '@petrhythm/data';
import { Button, Card, EmptyState, ErrorState, SectionHeading, t } from '@petrhythm/ui';
import { downloadText } from '../lib/download.js';
import { formatDate, formatDateTime } from '../lib/format.js';
import { usePlatform } from '../lib/platform-context.js';
import { useData } from './app.js';

const CADENCE_LABEL: Record<CareTimelineCadence, string> = { day: '오늘', week: '최근 7일', revisit: '지난 재진 이후' };
const KIND_LABEL: Record<CareTimelineFact['kind'], string> = { treatment: '투약·처치', meal: '식사·물', activity: '활동', scene: '돌봄 장면' };

export function CareTimelineScreen() {
  const platform = usePlatform();
  const { petId } = useParams();
  const { pets } = useData();
  const pet = pets.find((item) => item.id === petId);
  const [cadence, setCadence] = useState<CareTimelineCadence>('week');
  const [facts, setFacts] = useState<CareTimelineFact[]>([]);
  const [from, setFrom] = useState(0);
  const [visitBoundary, setVisitBoundary] = useState<number | undefined>();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [slideIndex, setSlideIndex] = useState(0);
  const [photoUrls, setPhotoUrls] = useState<Map<string, string>>(new Map());
  const [hiddenPhotos, setHiddenPhotos] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [message, setMessage] = useState('');

  const load = async () => {
    if (!petId) return;
    setLoading(true); setError(false);
    try {
      const [treatments, treatmentLogs, meals, gaitLogs, events, visits] = await Promise.all([
        repository.list<Treatment>('treatments'), repository.list<TreatmentLog>('treatmentLogs'), repository.list<Meal>('meals'),
        repository.list<GaitLog>('gaitLogs'), repository.list<PetEvent>('events'), repository.list<Visit>('visits'),
      ]);
      const timeline = buildCareTimeline({ petId, cadence, to: Date.now(), treatments, treatmentLogs, meals, gaitLogs, events, visits });
      const attachmentIds = [...new Set(timeline.facts.flatMap((fact) => fact.attachmentIds))];
      const attachments = await Promise.all(attachmentIds.map((id) => getAttachment(id)));
      const nextUrls = new Map<string, string>();
      attachments.forEach((attachment) => {
        if (attachment?.mime.startsWith('image/')) nextUrls.set(attachment.id, URL.createObjectURL(attachment.blob));
      });
      setPhotoUrls((previous) => { previous.forEach((url) => URL.revokeObjectURL(url)); return nextUrls; });
      setFacts(timeline.facts); setFrom(timeline.from); setVisitBoundary(timeline.visitBoundary);
      setSelected(new Set()); setSlideIndex(0); setHiddenPhotos(new Set());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [petId, cadence]);
  useEffect(() => () => { photoUrls.forEach((url) => URL.revokeObjectURL(url)); }, [photoUrls]);

  const selectedFacts = useMemo(() => facts.filter((fact) => selected.has(fact.id)), [facts, selected]);
  const slide = selectedFacts[Math.min(slideIndex, Math.max(0, selectedFacts.length - 1))];
  const rangeLabel = cadence === 'revisit' && !visitBoundary ? `병원 기록이 없어 최근 30일 · ${formatDate(from)}부터` : `${CADENCE_LABEL[cadence]} · ${formatDate(from)}부터`;
  const exportText = () => pet ? buildCareTimelineExport({ petName: pet.name, rangeLabel, facts, selectedIds: selected, formatDateTime }) : '';
  const share = async () => {
    try {
      const result = await platform.share.share({ title: `${pet?.name ?? ''} 재진 타임라인`, text: exportText() });
      setMessage(result === 'copied' ? t('timeline.copied') : t('timeline.shared'));
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === 'AbortError') return;
      setMessage(t('timeline.shareFailed'));
    }
  };
  const download = () => {
    if (!pet) return;
    downloadText(`petrhythm-${pet.name}-care-timeline.txt`, exportText());
    setMessage(t('timeline.downloaded'));
  };
  const toggle = (id: string) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSlideIndex(0);
    return next;
  });

  if (!pet) return <section className="screen container"><ErrorState messageKey="error.notFound" /></section>;
  if (loading) return <section className="screen container"><p>{t('common.loading')}</p></section>;
  if (error) return <section className="screen container"><ErrorState messageKey="error.loadFailed" onRetry={() => void load()} /></section>;

  return <section className="screen container care-timeline-screen">
    <Link className="back-link" to="/app">← 오늘</Link>
    <SectionHeading eyebrow={`${pet.name} · 재진 준비`} title={t('timeline.title')}><p className="screen-lede">{t('timeline.intro')}</p></SectionHeading>
    <Card className="care-timeline-trust"><strong>{t('timeline.trustTitle')}</strong><p>{t('timeline.trustBody')}</p><small>{t('timeline.permission')}</small></Card>
    <div className="care-timeline-toolbar" aria-label="기록 기간">{(Object.keys(CADENCE_LABEL) as CareTimelineCadence[]).map((item) => <button type="button" key={item} className={cadence === item ? 'choice selected' : 'choice'} aria-pressed={cadence === item} onClick={() => setCadence(item)}>{CADENCE_LABEL[item]}</button>)}</div>
    <p className="care-timeline-range">{rangeLabel}{cadence === 'revisit' && visitBoundary ? ` · ${formatDateTime(visitBoundary)} 방문 기록 기준` : ''}</p>
    {facts.length ? <div className="care-timeline-list">{facts.map((fact) => {
      const photoId = fact.attachmentIds.find((id) => photoUrls.has(id) && !hiddenPhotos.has(id));
      return <article className="care-timeline-row" key={fact.id}><time dateTime={new Date(fact.occurredAt).toISOString()}>{formatDateTime(fact.occurredAt)}</time><Card className={`care-fact-card${selected.has(fact.id) ? ' is-selected' : ''}`}>{photoId ? <img src={photoUrls.get(photoId)} alt="사용자가 첨부한 돌봄 장면" onError={() => setHiddenPhotos((current) => new Set(current).add(photoId))} /> : null}<div className="care-fact-body"><span>{KIND_LABEL[fact.kind]}</span><h2>{fact.title}</h2><p>{fact.detail}</p>{fact.note ? <small>{fact.note}</small> : null}</div><label className="care-fact-select"><input type="checkbox" checked={selected.has(fact.id)} onChange={() => toggle(fact.id)} /><span>회고·내보내기에 포함</span></label></Card></article>;
    })}</div> : <EmptyState title={t('timeline.empty')} description={t('timeline.emptyHint')} />}

    {slide ? <section className="care-recap" aria-labelledby="care-recap-title"><div className="section-row"><h2 id="care-recap-title">{t('timeline.recap')}</h2><span>{slideIndex + 1}/{selectedFacts.length}</span></div><Card className="care-recap-slide" aria-live="polite">{slide.attachmentIds.find((id) => photoUrls.has(id) && !hiddenPhotos.has(id)) ? <img src={photoUrls.get(slide.attachmentIds.find((id) => photoUrls.has(id) && !hiddenPhotos.has(id))!)} alt="사용자가 선택한 돌봄 장면" /> : <div className="care-recap-fact" aria-hidden><span>{KIND_LABEL[slide.kind]}</span></div>}<div><span>{formatDateTime(slide.occurredAt)}</span><h3>{slide.title}</h3><p>{slide.detail}</p>{slide.note ? <small>{slide.note}</small> : null}</div></Card><div className="care-recap-controls"><Button variant="secondary" disabled={slideIndex === 0} onClick={() => setSlideIndex((value) => Math.max(0, value - 1))}>이전</Button><Button variant="secondary" disabled={slideIndex >= selectedFacts.length - 1} onClick={() => setSlideIndex((value) => Math.min(selectedFacts.length - 1, value + 1))}>다음</Button></div><p className="form-hint">{t('timeline.manualOnly')}</p><div className="button-row"><Button onClick={() => void share()}>{t('timeline.share')}</Button><Button variant="secondary" onClick={download}>{t('timeline.download')}</Button></div></section> : <EmptyState title={t('timeline.selectEmpty')} description={t('timeline.selectHint')} />}
    {message ? <p className="toast" role="status">{message}</p> : null}
  </section>;
}
